import { useEffect, useRef } from "react";
import { Form, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { GoogleLogin, type CredentialResponse } from "@react-oauth/google";
import dentallyLogo from "../../../assets/dentally-logo.png";
import { selectGoogleLogin, selectLogin } from "./selector";
import { saveLogin, saveGoogleLogin } from "./slice";
import { stripWhitespace } from "../../../utils/formNormalizers";
import type { LoginFormValues } from "./types";
import {
  FormCard,
  ForgotLink,
  ForgotRow,
  GoogleLoginWrapper,
  ImageCard,
  LeftCol,
  LogoImage,
  OrDivider,
  PageRow,
  PrimaryButton,
  RightCol,
  StyledCheckbox,
  StyledForm,
  StyledInput,
  StyledPasswordInput,
  StyledTitle,
  SubmitFormItem,
  SubtitleLink,
  SubtitleRow,
  SubtitleText,
  TermsItem,
  TermsLink,
  TermsText,
} from "./style";

const { Item, useForm } = Form;

export default function LoginPage() {
  const [form] = useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const login = useSelector(selectLogin);
  const googleLogin = useSelector(selectGoogleLogin);
  const wasSavingLogin = useRef(false);
  const wasSavingGoogle = useRef(false);

  useEffect(() => {
    if (
      wasSavingLogin.current &&
      !login.saving &&
      !login.error &&
      login.data?.token
    ) {
      message.success(`Welcome back, ${login.data.full_name}`);
      navigate("/dashboard");
    }
    wasSavingLogin.current = login.saving;
  }, [login, navigate]);

  useEffect(() => {
    if (
      wasSavingGoogle.current &&
      !googleLogin.saving &&
      !googleLogin.error &&
      googleLogin.data?.token
    ) {
      message.success(`Welcome, ${googleLogin.data.full_name}`);
      navigate("/dashboard");
    }
    wasSavingGoogle.current = googleLogin.saving;
  }, [googleLogin, navigate]);

  function handleFinish(values: unknown) {
    const { email, password } = values as LoginFormValues;
    dispatch(saveLogin({ email, password }));
  }

  function handleGoogleSuccess({ credential }: CredentialResponse) {
    if (!credential) {
      message.error("Google sign-in returned no credential.");
      return;
    }
    dispatch(saveGoogleLogin({ token: credential }));
  }

  function handleGoogleError() {
    message.error("Google sign-in was cancelled or failed.");
  }

  return (
    <PageRow>
      <LeftCol xs={0} sm={0} md={0} lg={12} xl={12}>
        <ImageCard />
      </LeftCol>

      <RightCol xs={24} sm={24} md={24} lg={12} xl={12}>
        <FormCard vertical align="flex-start">
          <LogoImage
            src={dentallyLogo}
            alt="Dentally"
            width={233}
            preview={false}
          />

          <StyledTitle level={1}>Log in</StyledTitle>

          <SubtitleRow gap={5}>
            <SubtitleText>Don't have an account?</SubtitleText>
            <SubtitleLink href="/signup">Create an account</SubtitleLink>
          </SubtitleRow>

          <StyledForm
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            requiredMark={false}
          >
            <Item
              name="email"
              label="Email Address"
              normalize={stripWhitespace}
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Enter a valid email" },
              ]}
            >
              <StyledInput
                placeholder="Enter your email address"
                autoComplete="email"
              />
            </Item>

            <Item
              name="password"
              label="Password"
              validateFirst
              normalize={stripWhitespace}
              rules={[
                { required: true, message: "Please enter your password" },
              ]}
            >
              <StyledPasswordInput
                placeholder="Enter your password"
                autoComplete="current-password"
              />
            </Item>

            <ForgotRow>
              <ForgotLink href="/forgot-password">Forgot Password</ForgotLink>
            </ForgotRow>

            <TermsItem
              name="acceptTerms"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(
                            "You must accept the Terms & Conditions to continue",
                          ),
                        ),
                },
              ]}
            >
              <StyledCheckbox>
                <TermsText>I agree to the</TermsText>
                <TermsLink
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/terms-and-conditions");
                  }}
                >
                  Terms &amp; Condition
                </TermsLink>
              </StyledCheckbox>
            </TermsItem>

            <SubmitFormItem>
              <PrimaryButton
                type="primary"
                htmlType="submit"
                block
                loading={login.saving}
              >
                Log in
              </PrimaryButton>
            </SubmitFormItem>
          </StyledForm>

          <OrDivider>or</OrDivider>

          <GoogleLoginWrapper justify="center">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              useOneTap={false}
              theme="outline"
              size="large"
              text="continue_with"
              shape="pill"
              width="370"
            />
          </GoogleLoginWrapper>
        </FormCard>
      </RightCol>
    </PageRow>
  );
}
